import { afterEach, beforeEach, describe, expect, it } from '@jest/globals';
import type { GeneratedConfig } from '@ms-cloudpack/common-types';
import { readJsonSync, writeJsonSync } from '@ms-cloudpack/json-utilities';
import { createTempDir, removeTempDir } from '@ms-cloudpack/test-utilities';
import fs from 'fs';
import { dedent } from 'ts-dedent';
import { getConfigPath } from '../getConfigPath.js';
import { fileHeaderComment, writeGeneratedConfig } from './writeGeneratedConfig.js';

describe('writeGeneratedConfig', () => {
  let testPath: string;
  /** current generated config path under testPath */
  let configPath: string;

  function readConfigText() {
    return fs.readFileSync(configPath, 'utf8');
  }

  function readConfigJson() {
    return readJsonSync(configPath, { mode: 'permissive', throwOnError: true });
  }

  beforeEach(() => {
    testPath = createTempDir();
    configPath = getConfigPath(testPath).generatedConfigPath;
  });

  afterEach(async () => {
    await removeTempDir(testPath);
  });

  it('writes a new config file with sorted settings', async () => {
    const result = await writeGeneratedConfig(
      {
        packageSettings: [
          // Settings should end up sorted by name, and the keys should also be sorted.
          {
            exports: { '.': './foo.js' },
            excludedDependencies: ['b', 'a', 'c'],
            match: { name: 'foo', version: '^1.0.0' },
          },
          {
            includedDependencies: ['b', 'a', 'c'],
            match: { name: 'bar', version: '^1.0.0' },
            exports: { './foo': './foo.js' },
          },
        ],
      },
      testPath,
    );
    expect(result).toBe('written');

    // For this first test, check the whole file with a snapshot to verify that it has the
    // header comment and the formatting is correct.
    const configText = readConfigText();
    expect(configText).toMatchInlineSnapshot(`
      "/**
       * THIS FILE IS AUTO-GENERATED.
       * DO NOT MANUALLY EDIT THIS FILE.
       */
      {
        "$schema": "https://unpkg.com/@ms-cloudpack/cli/schema/GeneratedConfig.json",
        "packageSettings": [
          {
            "match": {
              "name": "bar",
              "version": "^1.0.0"
            },
            "exports": {
              "./foo": "./foo.js"
            },
            "includedDependencies": [
              "a",
              "b",
              "c"
            ]
          },
          {
            "match": {
              "name": "foo",
              "version": "^1.0.0"
            },
            "excludedDependencies": [
              "a",
              "b",
              "c"
            ],
            "exports": {
              ".": "./foo.js"
            }
          }
        ]
      }
      "
    `);

    // Explicitly verify that package setting keys have been sorted:
    // match first, then alphabetically by key name.
    const barMatchIndex = configText.indexOf('"name": "bar"');
    const barExportsIndex = configText.indexOf('exports');
    expect(barMatchIndex).toBeLessThan(barExportsIndex);
    expect(barExportsIndex).toBeLessThan(configText.indexOf('includedDependencies'));

    // Also explicitly verify that the package settings have been sorted
    expect(barMatchIndex).toBeLessThan(configText.indexOf('"name": "foo"'));
  });

  it('updates an existing config file with sorted settings', async () => {
    const config = dedent`
      {
        "packageSettings": [
          // hello
          { "match": { "name": "bar", version: "^1.0.0" }, "exports": { ".": "./bar.js" } },
          { "match": { "name": "foo", version: "^1.0.0" }, "exports": { ".": "./foo.js" } },
          { "match": { "name": "zzz", version: "^1.0.0" }, "exports": { ".": "./zzz.js" } }
        ]
      }`;
    fs.writeFileSync(configPath, config, 'utf8');

    const result = await writeGeneratedConfig(
      {
        packageSettings: [
          // different set of packages
          { match: { name: 'bar', version: '^1.0.0' }, exports: { '.': './qux.js' } },
          { match: { name: 'foo', version: '^1.0.0' }, exports: { '.': './foo.js' } },
          { match: { name: 'baz', version: '^1.0.0' }, exports: { '.': './index.js' } },
        ],
      },
      testPath,
    );
    expect(result).toBe('written');

    expect(readConfigJson()).toEqual({
      $schema: 'https://unpkg.com/@ms-cloudpack/cli/schema/GeneratedConfig.json',
      packageSettings: [
        // package names are sorted and zzz is gone
        { match: { name: 'bar', version: '^1.0.0' }, exports: { '.': './qux.js' } },
        { match: { name: 'baz', version: '^1.0.0' }, exports: { '.': './index.js' } },
        { match: { name: 'foo', version: '^1.0.0' }, exports: { '.': './foo.js' } },
      ],
    });

    // Check the text to verify that it has the header comment
    const newText = readConfigText();
    expect(newText.startsWith(fileHeaderComment)).toBe(true);
    // non-header comment is removed
    expect(newText).not.toContain('// hello');
  });

  it('writes a new config file with "clean" package settings', async () => {
    const result = await writeGeneratedConfig(
      {
        packageSettings: [
          { match: { name: 'foo', version: '^1.0.0' }, exports: { '.': './foo.js' } },
          // empty settings will be removed
          { match: { name: 'bar', version: '^1.0.0' } },
          { match: { name: 'bar', version: '^1.0.0' }, exports: {} },
          { match: { name: 'bar', version: '^1.0.0' }, excludedDependencies: [], includedDependencies: [] },
          { match: { name: 'bar', version: '^1.0.0' }, dynamicImports: [] },
        ],
      },
      testPath,
    );
    expect(result).toBe('written');

    expect(readConfigJson()).toEqual({
      $schema: 'https://unpkg.com/@ms-cloudpack/cli/schema/GeneratedConfig.json',
      packageSettings: [
        // the empty setting for bar is removed
        { match: { name: 'foo', version: '^1.0.0' }, exports: { '.': './foo.js' } },
      ],
    });
  });

  it('updates an existing config file with "clean" package settings', async () => {
    const config: GeneratedConfig = {
      packageSettings: [
        // this is preserved
        { match: { name: 'bar', version: '^1.0.0' }, exports: { '.': './bar.js' } },
        // these will be removed
        { match: { name: 'foo', version: '^1.0.0' } },
        { match: { name: 'zzz', version: '^1.0.0' } },
      ],
    };
    // manually write it with some settings that are not necessary
    writeJsonSync(configPath, config);

    // re-write it with cleaning first
    const result = await writeGeneratedConfig(config, testPath);
    expect(result).toBe('written');

    expect(readConfigJson()).toEqual({
      $schema: 'https://unpkg.com/@ms-cloudpack/cli/schema/GeneratedConfig.json',
      packageSettings: [{ match: { name: 'bar', version: '^1.0.0' }, exports: { '.': './bar.js' } }],
    });
  });

  it("does nothing if the config didn't previously exist and there are no settings after cleaning", async () => {
    const result = await writeGeneratedConfig(
      {
        // empty setting will be removed
        packageSettings: [{ match: { name: 'foo', version: '^1.0.0' } }],
      },
      testPath,
    );
    expect(result).toBe('no-op');
    expect(fs.existsSync(configPath)).toBe(false);
  });

  it('deletes an existing config file if there are no settings after cleaning', async () => {
    const oldConfig: GeneratedConfig = {
      packageSettings: [{ match: { name: 'bar', version: '^1.0.0' }, exports: { '.': './index.js' } }],
    };
    writeJsonSync(configPath, oldConfig);

    // New: the generated setting for 'bar' is no longer needed, and 'foo' ended up with this
    // empty setting which wil be removed by writeGeneratedConfig.
    const result = await writeGeneratedConfig(
      {
        packageSettings: [{ match: { name: 'foo', version: '^1.0.0' } }],
      },
      testPath,
    );
    expect(result).toBe('deleted');
    expect(fs.existsSync(configPath)).toBe(false);
  });
});
